import { beTableAsComponent } from 'components/BeTable';
import Date, { DATE_TIME } from 'components/Date';
import ProcessingButton from 'components/ProcessingButton';
import TriStateCheckbox, { ALL, NONE, PARTIAL, TriStateCheckboxState } from 'components/TriStateCheckbox';
import { cast } from 'component_utils/utils';
import { IIProductionDao$ISaveTimeWorkedFlushingError, ISimpleUserDetails, IProductionResource, IWorkOrder } from 'interfaces';
import { IProductionWorkTime } from 'interfaces/entities-production';
import { fromPairs, omit, sortBy } from 'lodash';
import moment from 'moment';
import React, { FC, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaEdit, FaExclamationTriangle, FaSave, FaTrash } from 'react-icons/fa';
import { Button, ButtonGroup, UncontrolledTooltip } from 'reactstrap';
import { getLeftPaddingForLevel } from 'styling/stylingVars';
import { ApiLoaderFun, useApi, useApiLoader } from 'utils/API';
import { IWorkOrderStepMap } from 'utils/endpoints/Production';
import Translation from 'utils/Language/Translation';
import { addNotification } from 'utils/NotificationManager';
import Editor from './Editor';
import SimpleModal, { ModalRef } from 'components/SimpleModal';
import If from 'components/If';
import { confirm } from 'utils/Prompts';

interface CIProductionWorkTime extends IProductionWorkTime {
  workOrderVisualCode: string;
}

const getWorkTimes: ApiLoaderFun<IProductionWorkTime[]> = async (api) =>
  sortBy(await api.production.getWorkTimes(), (it) => it.endTime || moment().toDate()).reverse();
const getWorkTimesForEveryone: ApiLoaderFun<IProductionWorkTime[]> = async (api) =>
  sortBy(await api.production.getAllWorkTimes(), (it) => it.endTime || moment().toDate()).reverse();
const getStateForValues = (values: boolean[], ifEmpty: TriStateCheckboxState): TriStateCheckboxState => {
  if (values.length === 0) {
    return ifEmpty;
  }

  const every = values.every((it) => it);
  const some = values.some((it) => it);
  if (every) {
    return ALL;
  } else if (some) {
    return PARTIAL;
  } else {
    return NONE;
  }
};

const generateTable = (all: boolean, allowFlush: boolean) => {
  interface TableProps {
    errors: IIProductionDao$ISaveTimeWorkedFlushingError[];
    reload: any;
    saveEntry: (entry: CIProductionWorkTime) => any;
    workOrders: IWorkOrder[];
    workOrderSteps: IWorkOrderStepMap;
    workCenters: IProductionResource[];
    isLoadingMetaData: boolean;
    checked: any;
    setChecked: (v: any) => any;
  }

  return beTableAsComponent<CIProductionWorkTime, TableProps>({
    tableId: `workConfirmationTable-${all}`,
    ignoreTableIdCheck: true,
    listLayoutBelow: 'md',
    getRowClasses: ({ row, props: { errors } }) => {
      if (errors.find(it => it.id === row.id)) {
        return ['text-danger']
      }
      return []
    },
    columns: [
      {
        Header: ({ rows, checked, setChecked, isListLayout }) => {
          if (allowFlush) {
            const values = rows.map((it) => checked[it.id] as boolean);
            const state = getStateForValues(values, NONE);
            return (
              <>
                <If cond={!isListLayout}>
                  <TriStateCheckbox
                    state={state}
                    onChange={(e) => {
                      e.stopPropagation();
                      setChecked((old: any) => {
                        if (state === NONE) {
                          return fromPairs(rows.map((it) => [it.id, true]));
                        } else {
                          return {};
                        }
                      });
                    }}
                  />
                </If>
                <Translation name="T.objects.workConfirmation.workOrderCode" />
              </>
            );
          }
          return <Translation name="T.objects.workConfirmation.workOrderCode" />;
        },
        value: (row) => row.workOrderVisualCode,
        Cell: ({ level, row, checked, setChecked, errors }) => {
          let checkbox = null;
          if (allowFlush) {
            const values = [checked[row.id] as boolean];
            const state = getStateForValues(values, NONE);
            checkbox = (
              <TriStateCheckbox
                state={state}
                onChange={(e) => {
                  e.stopPropagation();
                  setChecked((old: any) => {
                    const gen = { ...old };
                    gen[row.id] = state === NONE ? true : false
                    return gen;
                  });
                }}
              />
            );
          }

          const error = errors.find(it => it.id === row.id)
          let errorComponent = null
          if (error) {
            const target = "error_tooltipl_" + row.id
            errorComponent = (
              <>
                <FaExclamationTriangle id={target}/>
                <UncontrolledTooltip placement="top" target={target}>
                  {error.msg}
                </UncontrolledTooltip>
              </>
            )
          }

          return (
            <div style={{ paddingLeft: getLeftPaddingForLevel(level) }}>
              {checkbox}
              {errorComponent}
              {row.workOrderVisualCode}
            </div>
          );
        },
        filterable: true,
      },
      {
        Header: () => <Translation name="T.objects.workConfirmation.workOrderStep" />,
        value: (row) => row.workOrderStep,
        Cell: ({ row, workOrderSteps }) => {
          const step = (workOrderSteps[row.workOrderCode] || []).find((it) => it.stepCode === row.workOrderStep);
          return (
            <>
              {step ? `${step.stepCode} - ${step.description}` : row.workOrderStep}
            </>
          );
        },
        filterable: true,
      },
      {
        Header: () => <Translation name="T.objects.workConfirmation.user" />,
        value: (row) => cast<ISimpleUserDetails>(cast<any>(row).user).username,
        filterable: true,
        show() {
          return all
        },
      },
      {
        Header: () => <Translation name="T.objects.workConfirmation.workCenter" />,
        value: (row) => row.workCenter,
        Cell: ({ row, workCenters }) => {
          if (!row.workCenter) {
            return <>{'-'}</>
          }
          const wc = workCenters.find((it) => it.code === row.workCenter);
          return <>{wc ? `${wc.code} - ${wc.name}` : row.workCenter}</>;
        },
        filterable: true,
      },
      {
        Header: () => <Translation name="T.objects.workConfirmation.startTime" />,
        value: (row) => row.startTime,
        Cell: ({ row }) => <Date val={row.startTime} format={DATE_TIME} />,
        filterable: true,
      },
      {
        Header: () => <Translation name="T.objects.workConfirmation.endTime" />,
        value: (row) => row.endTime || '',
        Cell: ({ row, reload }) => {
          const api = useApi();
          if (!row.endTime) {
            return (
              <ProcessingButton
                color="danger"
                block
                className="py-0"
                onClick={async () => {
                  await api.production.saveWorkTimes({
                    ...cast<IProductionWorkTime>(omit(row, 'user')),
                    endTime: moment().toDate(),
                  });
                  reload();
                }}
              >
                <Translation name="T.misc.stop" />
              </ProcessingButton>
            );
          }
          return <Date val={row.endTime} format={DATE_TIME} />;
        },
        filterable: true,
      },
      {
        Header: () => <Translation name="T.objects.workConfirmation.totalTime" />,
        Cell: ({ row }) => {
          let minutes = null;
          minutes = moment.duration(
            (row.endTime ? moment(row.endTime) : moment()).diff(moment(row.startTime))
          ).asMinutes();
          return (
            !isNaN(minutes) &&
            minutes !== null && <Translation name="T.misc.minutes" params={{ minutes: minutes?.toFixed(0) }} />
          );
        },
        className: 'text-right',
      },
      {
        Header: () => <Translation name="T.misc.actions"/>,
        Cell: ({ row, reload, saveEntry, isLoadingMetaData, workOrders, workOrderSteps, workCenters }) => {
          const api = useApi();
          return (
            <ButtonGroup className='w-100 py-0'>
              <Editor
                trigger={(open) => (
                  <ProcessingButton disabled={isLoadingMetaData} color="info" className="py-0" onClick={open}>
                    <FaEdit />
                  </ProcessingButton>
                )}
                getTemplate={() => {
                  const pwt = row;

                  return {
                    id: pwt.id,
                    workOrderCode: pwt.workOrderCode,
                    workOrderStep: pwt.workOrderStep,
                    licensePlate: '',
                    workCenter: pwt.workCenter,
                    produced: pwt.produced,
                    scrap: pwt.scrap,
                    workTimeType: pwt.workTimeType,
                    startTime: moment(pwt.startTime).toDate(),
                    endTime: pwt.endTime ? moment(pwt.endTime).toDate() : null,
                  };
                }}
                workOrders={workOrders}
                workOrderSteps={workOrderSteps}
                workCenters={workCenters}
                reload={reload}
              />
              <If cond={allowFlush}>
                <ProcessingButton
                  color="success"
                  className="py-0"
                  onClick={async () => {
                    await saveEntry(row)
                    reload();
                  }}
                >
                  <FaSave />
                </ProcessingButton>
              </If>
              <ProcessingButton
                color="danger"
                className="py-0"
                onClick={async () => {
                  await api.production.deleteWorkTimes(row.id);
                  reload();
                }}
              >
                <FaTrash />
              </ProcessingButton>
            </ButtonGroup>
          );
        },
        width: 120 + (allowFlush ? 40 : 0),
        fixedWidth: true,
        resizable: false,
      },
    ],
  });
};

const View: FC<{ all?: boolean; allowFlush?: boolean }> = ({ all, allowFlush }) => {
  const {
    data: { workOrders, workOrderSteps, workCenters },
    isLoading: isLoadingMetaData,
  } = useApiLoader(
    useCallback(async (api) => {
      const [workCenters, workOrders] = await Promise.all([
        api.production.getResources(),
        api.production.getWorkOrders(),
      ]);
      const workOrderSteps = await api.production.getWorkOrderStepsForMultipleAsMap(workOrders.map((it) => it.code));
      return {
        workOrders,
        workOrderSteps,
        workCenters,
      };
    }, []),
    { workOrders: [], workOrderSteps: {}, workCenters: [] },
    true,
  );
  const {
    data: worked,
    isLoading: isLoadingWorked,
    reload,
  } = useApiLoader(all ? getWorkTimesForEveryone : getWorkTimes, [], true);
  const [checked, setChecked] = useState<any>({});
  const [errors, setErrors] = useState<IIProductionDao$ISaveTimeWorkedFlushingError[]>([])
  const api = useApi();

  const completeWorked = useMemo(() => {
    if (isLoadingMetaData || isLoadingWorked) {
      return []
    }

    return worked.map(it => ({
      ...it,
      workOrderVisualCode: workOrders.find(wo => wo.code === it.workOrderCode)?.visualCode
    }))
  }, [isLoadingMetaData, isLoadingWorked, worked, workOrders])

  const Table = useMemo(() => {
    return generateTable(all, allowFlush);
  }, [all, allowFlush]);

  return (
    <div className="flex-container">
      <div className={'nested-flex-container'}>
        <Table
          loading={isLoadingMetaData || isLoadingWorked}
          data={completeWorked}
          getExtraProps={() => ({
            errors,
            reload,
            workOrders,
            workOrderSteps,
            workCenters,
            isLoadingMetaData,
            checked,
            setChecked,
            async saveEntry(entry) {
              if (!await confirm(<Translation name="T.misc.areYouSure?"/>)) {
                return;
              }

              if (!entry.endTime) {
                await api.production.saveWorkTimes({
                  ...cast<IProductionWorkTime>(omit(entry, 'user')),
                  endTime: moment().toDate(),
                });
                reload();
              }

              const result = await api.production.flushWorkTimes([entry.id]);
              if (result.length > 0) {
                addNotification('danger', <Translation name="T.errors.operationCompletedWithErrors"/>)
              }
              setErrors(result)
              reload();
            },
          })}
        />
      </div>

      <ButtonGroup className="w-100">
        <Editor
          trigger={(open) => (
            <Button color="primary" disabled={isLoadingMetaData} onClick={open}>
              <Translation name="T.misc.add" />
            </Button>
          )}
          getTemplate={() => ({ workTimeType: 'WORK', startTime: moment().toDate() } as IProductionWorkTime)}
          workOrders={workOrders}
          workOrderSteps={workOrderSteps}
          workCenters={workCenters}
          reload={reload}
        />
        {allowFlush && (
          <ProcessingButton
            color="secondary"
            onClick={async () => {
              const selectedLines = worked.filter((it) => checked[it.id]);
              const result = await api.production.flushWorkTimes(selectedLines.map((it) => it.id));
              if (result.length > 0) {
                addNotification('danger', <Translation name="T.errors.operationCompletedWithErrors"/>)
              }
              setErrors(result)
              reload();
            }}
          >
            <Translation name="T.misc.flush" />
          </ProcessingButton>
        )}
      </ButtonGroup>
    </div>
  );
};

export const WorkConfirmationAdminView = () => {
  return <View all allowFlush />;
};

export const WorkConfirmationUserView = () => {
  return <View allowFlush/>;
};

export let openWorkTimeModal = () => {}
export const WorkConfirmationUserViewModal: FC<{ innerRef: MutableRefObject<ModalRef> }> = (({ innerRef }) => {
  const ref = useRef<ModalRef>();
  useEffect(() => {
    openWorkTimeModal = () => ref.current?.toggleOpen?.()
  })
  return (
    <SimpleModal
      title={<Translation name="T.menu.timeMeasurements" />}
      fullscreen
      ref={e => {
        innerRef.current = e;
        ref.current = e;
      }}
    >
      <WorkConfirmationUserView />
    </SimpleModal>
  )
})